/**
 * Notifications Settings Page
 * Configure personal notification preferences
 */

import React, { useState, useEffect } from 'react';
import {
  Bell, Mail, MessageSquare, Phone, Calendar, Users, FileText,
  Building, TrendingUp, AlertCircle, Save, RefreshCw, Smartphone, Volume2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

// Phase 1 CRM Enhancement - Push Notifications
import NotificationPreferences from '../components/NotificationPreferences';
// Per-category notification tones (built-in + custom uploads)
import NotificationToneSettings from '../components/NotificationToneSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationCategory = ({ 
  icon: Icon, 
  title, 
  description, 
  settings, 
  onToggle,
  categoryKey 
}) => (
  <Card data-testid={`notification-category-${categoryKey}`}>
    <CardHeader className="pb-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <div>
          <CardTitle className="text-base">{title}</CardTitle>
          <CardDescription className="text-sm">{description}</CardDescription>
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      {Object.entries(settings).map(([key, value]) => (
        <div key={key} className="flex items-center justify-between py-2 border-b last:border-0">
          <div>
            <p className="font-medium text-sm">{value.label}</p>
            <p className="text-xs text-muted-foreground">{value.description}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <Switch 
                checked={value.email} 
                onCheckedChange={(checked) => onToggle(categoryKey, key, 'email', checked)}
                data-testid={`${categoryKey}-${key}-email`}
              />
            </div>
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-muted-foreground" />
              <Switch 
                checked={value.push} 
                onCheckedChange={(checked) => onToggle(categoryKey, key, 'push', checked)}
                data-testid={`${categoryKey}-${key}-push`}
              />
            </div>
          </div>
        </div>
      ))}
    </CardContent>
  </Card>
);

export default function NotificationSettings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [digestFrequency, setDigestFrequency] = useState('daily');
  const [quietHoursStart, setQuietHoursStart] = useState('22:00');
  const [quietHoursEnd, setQuietHoursEnd] = useState('08:00');
  const [quietHoursEnabled, setQuietHoursEnabled] = useState(false);

  const [notifications, setNotifications] = useState({
    leads: {
      new_lead: { label: 'New Lead Assigned', description: 'When a new lead is assigned to you', email: true, push: true },
      lead_activity: { label: 'Lead Activity', description: 'When leads take actions (open email, visit website)', email: false, push: true },
      lead_status_change: { label: 'Lead Status Change', description: 'When lead status changes', email: true, push: true },
      follow_up_reminder: { label: 'Follow-up Reminders', description: 'Scheduled follow-up notifications', email: true, push: true },
    },
    deals: {
      deal_created: { label: 'New Deal', description: 'When a new deal is created', email: true, push: true },
      deal_stage_change: { label: 'Deal Stage Change', description: 'When deals move to a new stage', email: true, push: true },
      deal_won: { label: 'Deal Won', description: 'Celebrate when deals are won', email: true, push: true },
      deal_lost: { label: 'Deal Lost', description: 'When deals are marked as lost', email: true, push: false },
    },
    communication: {
      new_message: { label: 'New Messages', description: 'WhatsApp, Email, or Chat messages', email: false, push: true },
      missed_call: { label: 'Missed Calls', description: 'When you miss a call', email: true, push: true },
      voicemail: { label: 'New Voicemail', description: 'When you receive a voicemail', email: true, push: true },
    },
    meetings: {
      meeting_scheduled: { label: 'Meeting Scheduled', description: 'When a new meeting is scheduled', email: true, push: true },
      meeting_reminder: { label: 'Meeting Reminders', description: '15 minutes before meeting starts', email: false, push: true },
      meeting_cancelled: { label: 'Meeting Cancelled', description: 'When meetings are cancelled', email: true, push: true },
    },
    team: {
      team_mention: { label: 'Team Mentions', description: 'When someone mentions you', email: true, push: true },
      task_assigned: { label: 'Task Assigned', description: 'When a task is assigned to you', email: true, push: true },
      announcement: { label: 'Team Announcements', description: 'Important team updates', email: true, push: true },
    },
    system: {
      weekly_digest: { label: 'Weekly Digest', description: 'Summary of your weekly activity', email: true, push: false },
      security_alerts: { label: 'Security Alerts', description: 'Login from new device, password changes', email: true, push: true },
      product_updates: { label: 'Product Updates', description: 'New features and improvements', email: true, push: false },
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notification-settings?token=${token}`);
      const data = await response.json();
      if (data.success && data.data) {
        const settings = data.data;
        setDigestFrequency(settings.digest_frequency || 'daily');
        setQuietHoursEnabled(settings.quiet_hours_enabled || false);
        setQuietHoursStart(settings.quiet_hours_start || '22:00');
        setQuietHoursEnd(settings.quiet_hours_end || '08:00');
        if (settings.notifications) {
          setNotifications(settings.notifications);
        }
      }
    } catch (err) {
      toast.error('Failed to load notification settings');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (category, setting, channel, value) => {
    setNotifications(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: {
          ...prev[category][setting],
          [channel]: value
        }
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/notification-settings?token=${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          digest_frequency: digestFrequency,
          quiet_hours_enabled: quietHoursEnabled,
          quiet_hours_start: quietHoursStart,
          quiet_hours_end: quietHoursEnd,
          notifications
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Notification settings saved successfully');
      } else {
        toast.error(data.error || 'Failed to save settings');
      }
    } catch (err) {
      toast.error('Failed to save settings');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleEnableAll = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notification-settings/enable-all?token=${token}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        // Update local state
        const updated = { ...notifications };
        Object.keys(updated).forEach(category => {
          Object.keys(updated[category]).forEach(setting => {
            updated[category][setting].email = true;
            updated[category][setting].push = true;
          });
        });
        setNotifications(updated);
        toast.success('All notifications enabled');
      }
    } catch (err) {
      toast.error('Failed to enable notifications');
    }
  };

  const handleDisableAll = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notification-settings/disable-all?token=${token}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        // Update local state
        const updated = { ...notifications };
        Object.keys(updated).forEach(category => {
          Object.keys(updated[category]).forEach(setting => {
            // Keep security alerts on
            if (setting !== 'security_alerts') {
              updated[category][setting].email = false;
              updated[category][setting].push = false;
            }
          });
        });
        setNotifications(updated);
        toast.info('All notifications disabled (security alerts remain on)');
      }
    } catch (err) {
      toast.error('Failed to disable notifications');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto" data-testid="notification-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Notification Settings</h1>
          <p className="text-muted-foreground">Manage how and when you receive notifications</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleDisableAll}>
            Disable All
          </Button>
          <Button variant="outline" onClick={handleEnableAll}>
            Enable All
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-notifications-btn">
            {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Quick Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Delivery Settings</CardTitle>
          <CardDescription>Configure how notifications are delivered</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Email Digest Frequency</p>
              <p className="text-sm text-muted-foreground">How often to receive email summaries</p>
            </div>
            <Select value={digestFrequency} onValueChange={setDigestFrequency}>
              <SelectTrigger className="w-[180px]" data-testid="digest-frequency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="realtime">Real-time</SelectItem>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between py-4 border-t">
            <div>
              <p className="font-medium">Quiet Hours</p>
              <p className="text-sm text-muted-foreground">Pause non-urgent notifications</p>
            </div>
            <Switch 
              checked={quietHoursEnabled} 
              onCheckedChange={setQuietHoursEnabled}
              data-testid="quiet-hours-toggle"
            />
          </div>

          {quietHoursEnabled && (
            <div className="flex items-center gap-4 pl-4 pb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm">From</span>
                <Select value={quietHoursStart} onValueChange={setQuietHoursStart}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={`${i.toString().padStart(2, '0')}:00`}>
                        {`${i.toString().padStart(2, '0')}:00`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm">To</span>
                <Select value={quietHoursEnd} onValueChange={setQuietHoursEnd}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={`${i.toString().padStart(2, '0')}:00`}>
                        {`${i.toString().padStart(2, '0')}:00`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notification Categories */}
      <Tabs defaultValue="leads">
        <TabsList className="w-full justify-start flex-wrap h-auto gap-2 bg-transparent p-0">
          <TabsTrigger value="leads" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Users className="h-4 w-4 mr-2" />
            Leads
          </TabsTrigger>
          <TabsTrigger value="deals" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <TrendingUp className="h-4 w-4 mr-2" />
            Deals
          </TabsTrigger>
          <TabsTrigger value="communication" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <MessageSquare className="h-4 w-4 mr-2" />
            Communication
          </TabsTrigger>
          <TabsTrigger value="meetings" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Calendar className="h-4 w-4 mr-2" />
            Meetings
          </TabsTrigger>
          <TabsTrigger value="team" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Users className="h-4 w-4 mr-2" />
            Team
          </TabsTrigger>
          <TabsTrigger value="system" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <AlertCircle className="h-4 w-4 mr-2" />
            System
          </TabsTrigger>
          <TabsTrigger value="mobile" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Smartphone className="h-4 w-4 mr-2" />
            Mobile Push
          </TabsTrigger>
          <TabsTrigger value="sounds" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Volume2 className="h-4 w-4 mr-2" />
            Sounds
          </TabsTrigger>
        </TabsList>

        <TabsContent value="leads" className="mt-6">
          <NotificationCategory
            icon={Users}
            title="Lead Notifications"
            description="Stay updated on your leads and prospects"
            settings={notifications.leads}
            onToggle={handleToggle}
            categoryKey="leads"
          />
        </TabsContent>

        <TabsContent value="deals" className="mt-6">
          <NotificationCategory
            icon={TrendingUp}
            title="Deal Notifications"
            description="Track your deals through the pipeline"
            settings={notifications.deals}
            onToggle={handleToggle}
            categoryKey="deals"
          />
        </TabsContent>

        <TabsContent value="communication" className="mt-6">
          <NotificationCategory
            icon={MessageSquare}
            title="Communication Notifications"
            description="Messages, calls, and other communications"
            settings={notifications.communication}
            onToggle={handleToggle}
            categoryKey="communication"
          />
        </TabsContent>

        <TabsContent value="meetings" className="mt-6">
          <NotificationCategory
            icon={Calendar}
            title="Meeting Notifications"
            description="Meeting schedules and reminders"
            settings={notifications.meetings}
            onToggle={handleToggle}
            categoryKey="meetings"
          />
        </TabsContent>

        <TabsContent value="team" className="mt-6">
          <NotificationCategory
            icon={Users}
            title="Team Notifications"
            description="Collaboration and team updates"
            settings={notifications.team}
            onToggle={handleToggle}
            categoryKey="team"
          />
        </TabsContent>

        <TabsContent value="system" className="mt-6">
          <NotificationCategory
            icon={AlertCircle}
            title="System Notifications"
            description="Security, updates, and system alerts"
            settings={notifications.system}
            onToggle={handleToggle}
            categoryKey="system"
          />
        </TabsContent>

        {/* Phase 1: Mobile Push Notifications Tab */}
        <TabsContent value="mobile" className="mt-6">
          <NotificationPreferences />
        </TabsContent>

        {/* Per-category notification tones — built-in chimes the user
            can pick from a dropdown, plus per-device custom file
            uploads. Lets a CEO tell a follow-up reminder apart from a
            new lead without looking at the screen. */}
        <TabsContent value="sounds" className="mt-6">
          <NotificationToneSettings />
        </TabsContent>
      </Tabs>

      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-center gap-8 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span>Email notifications</span>
            </div>
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              <span>Push notifications</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
